import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import UNIDADES_MEDIDA from '../data/unidadesMedida';
import SignatureLines from './SignatureLines';
import ClientBlock from './ClientBlock';
import printWhenReady from '../utils/printWhenReady';
import './InvoicePrintView.css';
import { formatDay } from '../utils/dates';
import { useFileName, documentName, DOC_TYPES } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);
const unitName = (code) => UNIDADES_MEDIDA.find(u => u.code === code)?.name || '—';

const DISCLAIMER = 'El cliente confirma con su firma haber recibido los productos y las cantidades descritos en este conduce. Este documento no es una factura ni un comprobante fiscal.';

// The printable conduce (delivery note). Prices appear only when it was made with them.
const ConducePrintView = () => {
  const { id } = useParams();
  const [conduce, setConduce] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/conduces/${id}`).then(res => setConduce(res.data)).catch(err => {
      console.error('Error loading conduce:', err);
      setError('No se pudo cargar el conduce.');
    });
  }, [id]);

  useEffect(() => {
    if (conduce) {
      let cancelPrint = () => {};
      const t = setTimeout(() => { cancelPrint = printWhenReady(); }, 350);
      return () => { clearTimeout(t); cancelPrint(); };
    }
  }, [conduce]);

  // Saved as PDF: <Cliente>Conduce (utils/fileName.js).
  useFileName(conduce && documentName(conduce.client?.name, DOC_TYPES.conduce));

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!conduce) return <div className="p-4 text-muted">Cargando conduce...</div>;

  const client = conduce.client || {};
  const address = conduce.deliveryAddress || client.address;
  const prices = conduce.showPrices;

  return (
    <div className="invoice-page-wrapper">
      <div className="no-print text-center mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
      </div>

      <div className="invoice-paper">
        {/* .invoice-body flows normally; .invoice-bottom (signatures + thank-you note) is pinned to the bottom of the page in print. */}
        <div className="invoice-body">
        <div className="invoice-header-row">
          <div className="invoice-company-block">
            <img src="/CGCLogo.png" alt="Caribbean Gas Company" className="invoice-logo" />
            <div style={{ fontWeight: 700, color: '#0E1A24' }}>{COMPANY_INFO.name}</div>
            <div>RNC: {COMPANY_INFO.rnc}</div>
            <div>{COMPANY_INFO.address}</div>
            <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            <div>{COMPANY_INFO.website}</div>
          </div>
          <div className="invoice-meta-block">
            <div className="invoice-title">CONDUCE</div>
            <div>No.: {conduce.number}</div>
            <div>Fecha: {dateStr(conduce.createdAt)}</div>
            {conduce.reference && <div>Referencia: {conduce.reference}</div>}
          </div>
        </div>

        <ClientBlock label="Entregar a" client={client} address={address} />

        <table className="invoice-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Descripción</th>
              <th className="text-center">Cant.</th>
              <th>Unidad</th>
              {prices && <th className="text-end">Precio Unit.</th>}
              {prices && <th className="text-center">ITBIS</th>}
              {prices && <th className="text-end">Total</th>}
            </tr>
          </thead>
          <tbody>
            {conduce.items.map((item, i) => {
              const lineSubtotal = item.quantity * item.unitPrice;
              const lineItbis = item.itbisExento ? 0 : lineSubtotal * (item.itbisRate ?? 0.18);
              return (
                <tr key={i}>
                  <td>{item.sku || '—'}</td>
                  <td>{item.description}</td>
                  <td className="text-center">{item.quantity}</td>
                  <td>{unitName(item.unidadMedida)}</td>
                  {prices && <td className="text-end">{money(item.unitPrice)}</td>}
                  {prices && <td className="text-center">{money(lineItbis)}</td>}
                  {prices && <td className="text-end">{money(lineSubtotal + lineItbis)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>

        {prices && (
          <div className="invoice-totals">
            <div className="invoice-totals-box">
              <div className="invoice-totals-row"><span>Subtotal</span><span>{money(conduce.subtotal)}</span></div>
              <div className="invoice-totals-row"><span>ITBIS</span><span>{money(conduce.itbisTotal)}</span></div>
              <div className="invoice-totals-row grand"><span>Total</span><span>{money(conduce.total)}</span></div>
            </div>
          </div>
        )}

        {conduce.notes && (
          <div className="invoice-bill-to">
            <div className="label">Notas</div>
            <div style={{ whiteSpace: 'pre-line' }}>{conduce.notes}</div>
          </div>
        )}

        <div className="invoice-disclaimer">{DISCLAIMER}</div>

        </div>
        <div className="invoice-bottom">
        <SignatureLines clientName={client.name} />

        <div className="invoice-footer-note">Gracias por su preferencia — {COMPANY_INFO.name}</div>
        </div>
      </div>
    </div>
  );
};

export default ConducePrintView;
